import { useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useReactToPrint } from 'react-to-print';

import { Box, CircularProgress, Stack, styled } from '@mui/material';
import { motion } from 'framer-motion';

import { KaseApi } from '@/api';
import { FormRoot } from '@/components';
import { useCFSContext, useKaseContext } from '@/hooks';
import { IProperty, PropertyStatus } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { checkStatus } from '@/utils';

import { CustodyActions } from '../data';
import { useKasePropertyContext } from '../PropertyContext';
import { BulkActionsControlPopup } from './BulkActionsControlPopup';
import { PropertyLabelPrint } from './PropertyLabelPrint';

export const BulkActionsRoot = styled(Stack)(() => ({
  backgroundColor: colors.grey[100],
  padding: '10px',
  color: colors.white,
  borderRadius: '8px',
  '.selectWrapper>div': {
    marginBottom: 0,
  },
  '.MuiInputBase-root': {
    backgroundColor: `${colors.white}!important`,
    minWidth: '200px',
  },
}));

export const BulkActions = () => {
  const { updateToast } = useToastStore();
  const { kase } = useKaseContext();
  const { cfs } = useCFSContext();
  const { PropertyType } = useKaseMainContext();
  const {
    bulkSelection,
    bulkSelectionItems,
    setCustodyModalValues,
    resetBulkSelections,
    sourceAdapter,
    parentId,
  } = useKasePropertyContext();
  const [showCustodyActions, setShowCustodyActions] = useState<boolean>(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const methods = useForm({
    mode: 'all',
  });
  const { watch } = methods;
  const printLabelsRef = useRef<HTMLDivElement>(null);
  const [bulkPrintData, setBulkPrintData] = useState<Array<{
    property: {
      _id: string;
      number?: string;
      name?: string;
      typeTag?: string;
      involvement?: string;
      value?: number;
      description?: string;
      loggedInfo?: IProperty['loggedInfo'];
      createdBy?: IProperty['createdBy'];
      explosive?: {
        quantity?: number;
      };
      alcohol?: {
        quantity?: number;
        measurementType?: string;
      };
      drugs?: Array<{
        quantity?: number;
        measurementType?: string;
      }>;
    };
    cfsNumber?: string;
    propertyType?: string;
  }> | null>(null);

  const currentActionType = watch('bulkActionType');
  const actionTarget = watch('bulkActionTarget');

  const handleBulkPrint = useReactToPrint({
    contentRef: printLabelsRef,
    onAfterPrint: () => {
      setBulkPrintData(null);
      setIsPrintLoading(false);
      resetBulkSelections();
    },
  });

  const changeCustodyBtnStyles = {
    paddingRight: 3,
    marginRight: 3,
    borderRight: `1px solid ${colors.grey[90]}`,
    borderTopRightRadius: 0,
  };

  const releasedToLabOrCourt = useMemo(() => {
    return checkStatus(bulkSelectionItems, [
      PropertyStatus.RELEASED_TO_LAB,
      PropertyStatus.RELEASED_TO_COURT,
      PropertyStatus.TRANSFERRED,
    ]);
  }, [bulkSelectionItems]);

  const releasedToPerson = useMemo(() => {
    return checkStatus(bulkSelectionItems, PropertyStatus.RELEASED_TO_PERSON);
  }, [bulkSelectionItems]);

  const logged = useMemo(() => {
    return checkStatus(bulkSelectionItems, PropertyStatus.LOGGED);
  }, [bulkSelectionItems]);

  const preventChangeCustody = useMemo(() => {
    return checkStatus(bulkSelectionItems, [
      PropertyStatus.RELEASED_TO_OWNER,
      PropertyStatus.AUCTIONED,
      PropertyStatus.DISPOSED,
      PropertyStatus.DESTROYED,
    ]);
  }, [bulkSelectionItems]);

  const bulkActionTypeOptions = useMemo(() => {
    const options = [
      { label: 'Check In', value: 'check_in' },
      { label: 'Check Out', value: 'check_out' },
      { label: 'Move location', value: 'moveLocation' },
      { label: 'Final Disposition', value: 'release' },
    ];

    // If properties are RELEASED_TO_LAB or RELEASED_TO_COURT, exclude 'Check Out' and 'Move Location' options
    if (releasedToLabOrCourt) {
      return options.filter(
        (option) =>
          option.value !== 'check_out' && option.value !== 'moveLocation',
      );
    }

    if (releasedToPerson) {
      return options.filter(
        (option) =>
          option.value !== 'check_out' && option.value !== 'moveLocation',
      );
    }

    if (logged) {
      return options.filter((option) => option.value === 'check_in');
    }

    return options;
  }, [releasedToLabOrCourt]);

  const actionTargetItems = {
    check_in: [{ label: 'Check in', value: CustodyActions.CHECK_IN }],
    check_out: [
      { label: 'To Court', value: CustodyActions.TO_COURT },
      { label: 'To Lab', value: CustodyActions.TO_LAB },
      { label: 'To Person', value: CustodyActions.TO_PERSON },
      { label: 'To Other Agency', value: CustodyActions.TO_OTHER_AGENCY },
    ],
    moveLocation: [
      { label: 'Move Location', value: CustodyActions.MOVE_LOCATION },
    ],
    release: [
      { label: 'To Owner', value: CustodyActions.TO_OWNER },
      { label: 'Dispose', value: CustodyActions.DISPOSE },
      { label: 'Destroy', value: CustodyActions.DESTROY },
      { label: 'Auction', value: CustodyActions.AUCTION },
    ],
  };

  const getPropertyTypeLabel = useCallback(
    (typeKey?: string) => {
      if (!typeKey || !PropertyType?.size) return undefined;
      const allPropertyTypes = Array.from(PropertyType.values());
      return allPropertyTypes.find((value) => value.key === typeKey)?.label;
    },
    [PropertyType],
  );

  const handlePrintBulkLabels = useCallback(async () => {
    if (!parentId) {
      updateToast({ open: true, message: 'Invalid property parent ID' });
      return;
    }

    if (!bulkSelection.length) {
      updateToast({
        type: ConfirmTypes.WARNING,
        open: true,
        message: 'Please select properties to print',
      });
      return;
    }

    setIsPrintLoading(true);

    try {
      let cfsNumber: string | undefined;
      if (sourceAdapter.source === 'cfs') {
        cfsNumber = cfs?.number;
      } else {
        try {
          const cfsResponse = await KaseApi.getLinkedCFS(parentId);
          const cfsData =
            (cfsResponse?.data as any)?.results || cfsResponse?.data;
          cfsNumber = Array.isArray(cfsData)
            ? cfsData[0]?.cfs?.number
            : cfsData?.cfs?.number;
        } catch {
          cfsNumber = undefined;
        }
      }

      const allPrintData = await Promise.all(
        bulkSelection.map(async (propertyId) => {
          const propertyResponse = await sourceAdapter.getOne(
            parentId,
            propertyId,
          );
          const property = propertyResponse.data;

          if (!property?._id) {
            throw new Error(`Property ${propertyId} is missing _id`);
          }

          return {
            property: {
              _id: property._id,
              number: property.number,
              name: property.name,
              typeTag: property.typeTag,
              involvement: property.involvement,
              value: property.value,
              description: property.description,
              loggedInfo: property.loggedInfo,
              createdBy: property.createdBy,
              explosive: property.explosive
                ? {
                    quantity: property.explosive.quantity ?? undefined,
                  }
                : undefined,
              alcohol: property.alcohol
                ? {
                    quantity: property.alcohol.quantity ?? undefined,
                    measurementType:
                      property.alcohol.measurementType ?? undefined,
                  }
                : undefined,
              drugs:
                property.drugs?.map((drug) => ({
                  quantity: drug.quantity ?? undefined,
                  measurementType: drug.measurementType ?? undefined,
                })) ?? undefined,
            },
            cfsNumber,
            propertyType: getPropertyTypeLabel(property.type),
          };
        }),
      );

      setBulkPrintData(allPrintData);
      setTimeout(() => {
        handleBulkPrint();
      }, 100);
      setIsPrintLoading(false);
    } catch (error: any) {
      setIsPrintLoading(false);
      updateToast({
        open: true,
        message: error.message || 'Failed to fetch property details',
      });
    }
  }, [
    bulkSelection,
    cfs?.number,
    getPropertyTypeLabel,
    handleBulkPrint,
    parentId,
    sourceAdapter,
    updateToast,
  ]);

  const handleClick = async (action: keyof typeof CustodyActions) => {
    if (action === CustodyActions.PRINT_LABELS) {
      await handlePrintBulkLabels();
      return;
    }

    setCustodyModalValues({
      open: true,
      custodyAction: action,
      bulkSelection, // To access selected properties IDs in the modal
      bulkSelectionItems, // To access selected properties in the modal
    });
    resetBulkSelections();
  };

  return (
    <motion.div
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'absolute',
        zIndex: 3,
        width: '70%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
      initial={{ bottom: '-200px' }}
      animate={{ bottom: '50px' }}
      exit={{ bottom: '-200px', opacity: 0 }}
      transition={{ ease: 'easeIn', duration: 0.2 }}
    >
      {isPrintLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}

      {bulkPrintData && (kase || cfs) && (
        <div style={{ display: 'none' }}>
          <div ref={printLabelsRef}>
            {bulkPrintData.map((printItem, index) => (
              <PropertyLabelPrint
                key={printItem.property._id || index}
                kase={{
                  number: sourceAdapter.source === 'kase' ? kase?.number : '',
                }}
                propertyDetails={printItem.property}
                cfsNumber={printItem.cfsNumber}
                propertyType={printItem.propertyType}
              />
            ))}
          </div>
        </div>
      )}

      <BulkActionsRoot>
        <FormProvider {...methods}>
          <FormRoot
            sx={{
              justifyContent: 'start',
              m: 0,
              p: 0,
              gap: 3,
              alignItems: 'center',
            }}
          >
            <BulkActionsControlPopup
              showCustodyActions={showCustodyActions}
              changeCustodyBtnStyles={changeCustodyBtnStyles}
              setShowCustodyActions={setShowCustodyActions}
              bulkActionTypeOptions={bulkActionTypeOptions}
              currentActionType={currentActionType}
              actionTargetItems={actionTargetItems}
              handleClick={handleClick}
              actionTarget={actionTarget}
              preventChangeCustody={preventChangeCustody}
            />
          </FormRoot>
        </FormProvider>
      </BulkActionsRoot>
    </motion.div>
  );
};
